
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Gift, Cake, Heart, Star, PartyPopper } from 'lucide-react';
import { getUpcomingSpecialDays, formatShortDate, isToday } from '@/utils/dateUtils';
import { SpecialDay } from '@/utils/types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface UpcomingSpecialDaysProps {
  specialDays: SpecialDay[];
  daysAhead?: number;
}

// Function to determine icon based on special day name
const getSpecialDayIcon = (name: string) => {
  const lowercaseName = name.toLowerCase();
  if (lowercaseName.includes('birthday')) return Cake;
  if (lowercaseName.includes('anniversary')) return Heart;
  if (lowercaseName.includes('promotion')) return Star;
  if (lowercaseName.includes('graduation')) return PartyPopper;
  return Gift; // Default icon
};

// Function to determine background color based on special day name
const getSpecialDayColor = (name: string) => {
  const lowercaseName = name.toLowerCase();
  if (lowercaseName.includes('birthday')) return 'bg-pink-100 text-pink-700';
  if (lowercaseName.includes('anniversary')) return 'bg-purple-100 text-purple-700';
  if (lowercaseName.includes('promotion')) return 'bg-blue-100 text-blue-700';
  if (lowercaseName.includes('graduation')) return 'bg-green-100 text-green-700';
  return 'bg-primary/10 text-primary'; // Default color
};

const UpcomingSpecialDays: React.FC<UpcomingSpecialDaysProps> = ({ 
  specialDays,
  daysAhead = 30
}) => {
  const upcomingDays = getUpcomingSpecialDays(
    specialDays.filter(day => day.enabled), 
    daysAhead
  );
  
  if (upcomingDays.length === 0) {
    return null;
  }
  
  return (
    <Card className="border-2 border-primary/20">
      <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardTitle className="text-lg flex items-center">
          <Calendar className="mr-2 h-5 w-5 text-primary" />
          Upcoming Special Days
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <ul className="space-y-4">
          {upcomingDays.map((day) => {
            const IconComponent = getSpecialDayIcon(day.name);
            const colorClasses = getSpecialDayColor(day.name);
            const isSpecialDayToday = isToday(day.date);
            
            return (
              <li 
                key={day.id} 
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg transition-all hover:scale-[1.02]",
                  isSpecialDayToday ? "bg-gradient-to-r from-primary/10 to-primary/5 animate-pulse" : ""
                )}
              >
                <div className="flex items-center">
                  <div className={cn(
                    "h-10 w-10 rounded-full flex items-center justify-center mr-3",
                    colorClasses
                  )}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">{day.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatShortDate(day.date)}
                    </p>
                  </div>
                </div>
                {isSpecialDayToday ? (
                  <Badge variant="default" className="px-3 py-1 bg-primary animate-pulse">
                    Today!
                  </Badge>
                ) : (
                  <Badge variant="outline" className={cn(
                    "px-3 py-1 border",
                    colorClasses.replace('bg-', 'border-')
                  )}>
                    {getDaysUntil(day.date)}
                  </Badge>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

// Helper function to calculate days until special day
const getDaysUntil = (dateString: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  
  // If the date is in the past for this year, we need to calculate for next year
  if (date < today && date.getMonth() < today.getMonth()) {
    date.setFullYear(today.getFullYear() + 1);
  } else if (date < today) {
    date.setFullYear(today.getFullYear());
  }
  
  const diffTime = Math.abs(date.getTime() - today.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Today!";
  if (diffDays === 1) return "Tomorrow!";
  return `In ${diffDays} days`;
};

export default UpcomingSpecialDays;
